import React, { Children, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import boTheme from '@ecp/common/src/style/theme/bo';
import { FlexBox, FlexBoxSpaceBetween, VerticalDivider } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { TextBox } from '@ecp/common/src/text/TextBox';
import Dropdown from '@ecp/common/src/components/dropdown/bo/Dropdown';
import Button from '@ecp/common/src/components/button/bo/Button';
import Tooltip from '@ecp/common/src/components/tooltip/bo/Tooltip';
import { GRID_SIZE, GRID_SIZE_ENL } from '@ecp/common/src/const/Paging';
import { ReactComponent as QuestionIcon } from '@ecp/common/src/assets/icon/icon__tooltip-question.svg';

import ExcelWork, { CustomExcelWork, ExcelDownload } from '@bo-components/common/grid/ExcelWork';
import { leftHeaderButtonPropTypes, rightHeaderButtonPropTypes } from '@bo-components/common/grid/gridPropTypes';

const ExcelButton = ({
  type,
  hasData,
  onExcelDownload,
  onExcelTemplateDownload,
  onExcelUpload,
  onExcelUploadWithMaxFileSize,
  customExcelForm,
}) => {
  const isSmallType = useMemo(() => type.includes('Small'), [type]);
  const isSimple = useMemo(() => type.includes('excelSimpleWork'), [type]);
  const isDisabledDownload = useMemo(() => type.includes('excelDisabledDownload'), [type]);
  
  return (
    <>
      {type.includes('download') && (
        <ExcelDownload isSmallType={isSmallType} hasData={hasData} onExcelDownload={onExcelDownload} />
      )}
      {type.includes('excel') && (
        <ExcelWork
          isIconType={isSmallType}
          isSimple={isSimple}
          isDisabledDownload={isDisabledDownload}
          handleExcelTemplateDownload={onExcelTemplateDownload}
          handleExcelUpload={onExcelUpload}
          handleExcelDownload={onExcelDownload}
          hasData={hasData}
        />
      )}
      {type.includes('custom') && (
        <CustomExcelWork
          customExcelForm={customExcelForm}
          handleExcelTemplateDownload={onExcelTemplateDownload}
          handleExcelUpload={onExcelUpload}
          handleExcelDownload={onExcelDownload}
          hasData={hasData}
        />
      )}
      {type.includes('size') && (
        <CustomExcelWork
          customExcelForm={customExcelForm}
          handleExcelTemplateDownload={onExcelTemplateDownload}
          handleExcelUpload={onExcelUploadWithMaxFileSize}
          handleExcelDownload={onExcelDownload}
          hasData={hasData}
        />
      )}
    </>
  );
};

ExcelButton.propTypes = {
  type: PropTypes.oneOf([
    undefined,
    'excelWork',
    'download',
    'excelSimpleWork',
    'excelWorkSmall',
    'downloadSmall',
    'excelSimpleWorkSmall',
    'excelDisabledDownload',
    'custom',
    'size',
  ]),
  hasData: PropTypes.bool,
  onExcelDownload: PropTypes.func,
  onExcelTemplateDownload: PropTypes.func,
  onExcelUpload: PropTypes.func,
  onExcelUploadWithMaxFileSize: PropTypes.func,
  customExcelForm: PropTypes.array,
};

const GridLeftHeader = ({
  grid,
  total,
  checkedRows,
  totalUnit,
  unPaged,
  hidePageSize,
  pageSize,
  onSizeChange,
  showDelete,
  isHeaderEnabled,
  onDeleteRows,
  showCopy,
  onCopyRows,
  headerButtonEnabled,
  leftChildren,
  sizeEnl,
  gridSizes, // ECMJ - 박준석
}) => (
  <FlexBox gap={'6px'}>
    {total !== undefined && (
      <TextBox size={boTheme.font.size.small} color={boTheme.color.sub1}>
        {checkedRows?.length === 0
          ? `총 ${Number(total).toLocaleString('ko-KR')}${totalUnit ?? '건'}`
          : `${Number(checkedRows?.length).toLocaleString('ko-KR')}${totalUnit ?? '개'} 선택`}
      </TextBox>
    )}
    {!unPaged && !hidePageSize && (
      <Dropdown
        width={'76px'}
        items={gridSizes ?? (sizeEnl ? GRID_SIZE_ENL : GRID_SIZE)} // ECMJ - 박준석
        value={pageSize}
        onChange={onSizeChange}
        disabled={Number(total) === 0}
      />
    )}
    {(total !== undefined || !unPaged) && <Spacing left={'2px'} />}
    {showDelete && (
      <Button type={'Third'} width={'42px'} disabled={!isHeaderEnabled} onClick={onDeleteRows}>
        삭제
      </Button>
    )}
    {showCopy && (
      <Button type={'Third'} width={'42px'} disabled={!isHeaderEnabled} onClick={onCopyRows}>
        복사
      </Button>
    )}
    {(showDelete || showCopy) && leftChildren.length > 0 && (
      <Spacing left={'6px'} right={'6px'} height={'100%'}>
        <VerticalDivider height={'12px'} />
      </Spacing>
    )}
    {leftChildren &&
      leftChildren.map((child, index) =>
        React.cloneElement(child, {
          key: `button-component__left__${index}`,
          disabled: !isHeaderEnabled && !headerButtonEnabled,
          checkedRows,
          grid,
        })
      )}
  </FlexBox>
);

GridLeftHeader.propTypes = {
  grid: PropTypes.object,
  total: PropTypes.number,
  checkedRows: PropTypes.array,
  totalUnit: PropTypes.string,
  unPaged: PropTypes.bool,
  hidePageSize: PropTypes.bool,
  pageSize: PropTypes.object,
  onSizeChange: PropTypes.func,
  showDelete: PropTypes.bool,
  isHeaderEnabled: PropTypes.bool,
  onDeleteRows: PropTypes.func,
  onCopyRows: PropTypes.func,
  leftChildren: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  gridSizes: PropTypes.array, // ECMJ - 박준석
  ...leftHeaderButtonPropTypes,
};

const GridRightHeader = ({
  excelTooltip,
  excel,
  total,
  customExcelForm,
  onExcelDownload,
  onExcelTemplateDownload,
  onExcelUpload,
  onExcelUploadWithMaxFileSize,
  rightChildren,
  showAddRow,
  showRegister,
  addRowLabel,
  onAddRow,
  onRegister,
  showChangeRow,
  changeRowLabel,
  changeRowOnClick,
}) => (
  <FlexBox gap={'6px'}>
    {excelTooltip && (
      <Tooltip arrow placement={'top-start'} value={excelTooltip}>
        <HelpIcon />
      </Tooltip>
    )}
    {excel && (
      <ExcelButton
        type={excel}
        hasData={!!total}
        customExcelForm={customExcelForm}
        onExcelDownload={onExcelDownload}
        onExcelTemplateDownload={onExcelTemplateDownload}
        onExcelUpload={onExcelUpload}
        onExcelUploadWithMaxFileSize={onExcelUploadWithMaxFileSize}
      />
    )}
    {rightChildren &&
      rightChildren.map((child, index) =>
        React.cloneElement(child, {
          key: `button-component__right__${index}`,
        })
      )}
    {(showAddRow || showRegister) && (excel || rightChildren.length) > 0 && (
      <Spacing left={'6px'} right={'6px'} height={'100%'}>
        <VerticalDivider height={'12px'} />
      </Spacing>
    )}
    {showAddRow && (
      <Button type="Third" padding={'0 7px'} width={'max-content'} onClick={onAddRow}>
        {addRowLabel}
      </Button>
    )}
    {showChangeRow && (
      <Button type="Third" padding={'0 7px'} width={'max-content'} onClick={changeRowOnClick}>
        {changeRowLabel}
      </Button>
    )}
    {showRegister && (
      <Button type="Primary" width={'55px'} onClick={onRegister}>
        등록
      </Button>
    )}
  </FlexBox>
);

const HelpIcon = styled(QuestionIcon)`
  width: 18px;
  height: 18px;
  margin: 0 6px;
  flex-shrink: 0;
  cursor: pointer;
`;

GridRightHeader.propTypes = {
  total: PropTypes.number,
  rightChildren: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  ...rightHeaderButtonPropTypes,
};

const GridHeader = ({
  grid,
  total,
  unPaged,
  pageSize,
  hidePageSize,
  showDelete,
  showCopy,
  isHeaderEnabled,
  headerButtonEnabled,
  checkedRows,
  excelTooltip,
  excel,
  addRowLabel,
  totalUnit,
  children,
  ...props
}) => {
  const {
    onSizeChange,
    onDeleteRows,
    onCopyRows,
    onExcelDownload,
    onExcelTemplateDownload,
    onExcelUpload,
    onExcelUploadWithMaxFileSize,
    showAddRow,
    onAddRow,
    showRegister,
    onRegister,
    showChangeRow,
    changeRowLabel,
    changeRowOnClick,
  } = props;

  const { leftChildren, rightChildren } = useMemo(
    () =>
      Children.toArray(children)?.reduce?.(
        (childrenMap, child) => {
          if (child.props.position === 'right') {
            childrenMap.rightChildren.push(child);
          } else {
            childrenMap.leftChildren.push(child);
          }
          return childrenMap;
        },
        { leftChildren: [], rightChildren: [] }
      ) || {},
    [children]
  );

  return (
    <FlexBoxSpaceBetween width={'100%'}>
      <GridLeftHeader
        grid={grid}
        total={total}
        checkedRows={checkedRows}
        totalUnit={totalUnit}
        unPaged={unPaged}
        hidePageSize={hidePageSize}
        pageSize={pageSize}
        onSizeChange={onSizeChange}
        showDelete={showDelete}
        isHeaderEnabled={isHeaderEnabled}
        onDeleteRows={onDeleteRows}
        showCopy={showCopy}
        onCopyRows={onCopyRows}
        headerButtonEnabled={headerButtonEnabled}
        leftChildren={leftChildren}
        sizeEnl={props?.sizeEnl}
        gridSizes={props?.gridSizes}
      />
      <GridRightHeader
        excelTooltip={excelTooltip}
        excel={excel}
        total={total}
        customExcelForm={props.customExcelForm}
        onExcelDownload={onExcelDownload}
        onExcelTemplateDownload={onExcelTemplateDownload}
        onExcelUpload={onExcelUpload}
        onExcelUploadWithMaxFileSize={onExcelUploadWithMaxFileSize}
        rightChildren={rightChildren}
        showAddRow={showAddRow}
        showRegister={showRegister}
        onAddRow={onAddRow}
        addRowLabel={addRowLabel}
        onRegister={onRegister}
        showChangeRow={showChangeRow}
        changeRowLabel={changeRowLabel}
        changeRowOnClick={changeRowOnClick}
      />
    </FlexBoxSpaceBetween>
  );
};

GridHeader.propTypes = {
  grid: PropTypes.object,
  total: PropTypes.number,
  unPaged: PropTypes.bool,
  hidePageSize: PropTypes.bool,
  pageSize: PropTypes.object,
  onSizeChange: PropTypes.func,
  isHeaderEnabled: PropTypes.bool,
  onDeleteRows: PropTypes.func,
  onCopyRows: PropTypes.func,
  onExcelTemplateDownload: PropTypes.func,
  onExcelUpload: PropTypes.func,
  onExcelUploadWithMaxFileSize: PropTypes.func,
  checkedRows: PropTypes.array,
  totalUnit: PropTypes.string,
  sizeEnl: PropTypes.string,
  ...leftHeaderButtonPropTypes,
  ...rightHeaderButtonPropTypes,
};

GridHeader.defaultProps = {
  addRowLabel: '행추가',
};

export default GridHeader;
